import type {
  IAPMEdgeDataService,
  ServiceContract,
  TemplateItemUpsertType,
} from '@cognite/apm-client';
import type { Edge, IFDMClient } from '@cognite/fdm-client';
import type { CogniteClient, NodeWrite } from '@cognite/sdk';

import type { InfieldViewReferences } from '../../constants/cdm-infield-data-model';
import type { CDMChecklistItemWithParentType } from '../checklist/cdm-types';
import type { MeasurementWithParent } from '../measurements/cdm-types';

import type {
  ReferenceChecklistItemsEdge,
  ReferenceMeasurementsEdge,
  ReferenceObservationsEdge,
  ReferenceSchedulesEdge,
  ReferenceTemplateItemsEdge,
} from './types';
import { createChildToParentDirectRelationship } from './utils/create-direct-relation-payload-from-edges';
import { separateEdgesByType } from './utils/separate-edges-by-type';

export type CogniteClientDependencies = {
  instances: Pick<CogniteClient['instances'], 'upsert'>;
};

export type ServiceContractDependencies = Omit<ServiceContract, 'fdmClient'> & {
  fdmClient: Pick<IFDMClient, 'upsertEdges'>;
};

export class CdmEdgeToDirectRelationService implements IAPMEdgeDataService {
  instanceSpace: string;
  modelSpace: string;
  modelVersion: string;
  fdmClient: Pick<IFDMClient, 'upsertEdges'>;
  viewReferences: InfieldViewReferences;
  cogniteClient: CogniteClientDependencies;
  constructor(
    contract: ServiceContractDependencies,
    viewReferences: InfieldViewReferences,
    cogniteClient: CogniteClientDependencies
  ) {
    this.instanceSpace = contract.instanceSpace;
    this.modelSpace = contract.modelSpace;
    this.modelVersion = contract.modelVersion;
    this.fdmClient = contract.fdmClient;
    this.viewReferences = viewReferences;
    this.cogniteClient = cogniteClient;
  }

  /**
   * Upserts edges by using different upsert strategies.
   * (e.g. some are converted to node upserts with direct relations, others are upserted as edges)
   * @param edges - Array of edges to upsert
   */
  async upsert(edges: Edge[]): Promise<void> {
    const {
      referenceChecklistItems = [],
      referenceTemplateItems = [],
      referenceMeasurements = [],
      referenceObservations = [],
      referenceSchedules = [],
    } = separateEdgesByType(edges);

    if (referenceChecklistItems.length > 0) {
      await this.upsertChecklistItemRelations(referenceChecklistItems);
    }

    if (referenceTemplateItems.length > 0) {
      await this.upsertTemplateItemRelations(referenceTemplateItems);
    }

    if (referenceMeasurements.length > 0) {
      await this.upsertMeasurementRelations(referenceMeasurements);
    }

    if (referenceSchedules.length > 0) {
      await this.upsertScheduleRelations(referenceSchedules);
    }

    if (referenceObservations.length > 0) {
      await this.upsertObservationEdges(referenceObservations);
    }
  }

  private async upsertChecklistItemRelations(
    edges: ReferenceChecklistItemsEdge[]
  ): Promise<void> {
    const directRelationNodesToUpsert = edges.map((edge) =>
      createChildToParentDirectRelationship(
        edge.startNode,
        edge.endNode,
        this.viewReferences.infieldChecklistItem,
        'checklist' satisfies keyof CDMChecklistItemWithParentType,
        this.instanceSpace
      )
    );

    await this.upsertRelationshipNodes(directRelationNodesToUpsert);
  }

  private async upsertTemplateItemRelations(
    edges: ReferenceTemplateItemsEdge[]
  ): Promise<void> {
    const directRelationNodesToUpsert = edges.map((edge) =>
      createChildToParentDirectRelationship(
        edge.startNode,
        edge.endNode,
        this.viewReferences.infieldTemplateItem,
        'template' satisfies keyof TemplateItemUpsertType,
        this.instanceSpace
      )
    );

    await this.upsertRelationshipNodes(directRelationNodesToUpsert);
  }

  private async upsertMeasurementRelations(
    edges: ReferenceMeasurementsEdge[]
  ): Promise<void> {
    const directRelationNodesToUpsert = edges.map((edge) =>
      createChildToParentDirectRelationship(
        edge.startNode,
        edge.endNode,
        this.viewReferences.infieldMeasurementReading,
        'item' satisfies keyof MeasurementWithParent,
        this.instanceSpace
      )
    );

    await this.upsertRelationshipNodes(directRelationNodesToUpsert);
  }

  private async upsertScheduleRelations(
    edges: ReferenceSchedulesEdge[]
  ): Promise<void> {
    await this.fdmClient.upsertEdges(
      edges,
      this.instanceSpace,
      this.modelSpace
    );
  }

  private async upsertObservationEdges(
    edges: ReferenceObservationsEdge[]
  ): Promise<void> {
    await this.fdmClient.upsertEdges(
      edges,
      this.instanceSpace,
      this.modelSpace
    );
  }

  private async upsertRelationshipNodes(nodes: NodeWrite[]): Promise<void> {
    await this.cogniteClient.instances.upsert({
      autoCreateDirectRelations: false,
      autoCreateEndNodes: false,
      autoCreateStartNodes: false,
      items: nodes,
    });
  }
}

export const createCdmEdgeToDirectRelationService = (
  ...args: ConstructorParameters<typeof CdmEdgeToDirectRelationService>
): IAPMEdgeDataService => new CdmEdgeToDirectRelationService(...args);
